import React, { useState, useEffect } from 'react';
import { withRouter, Link } from 'react-router-dom';
import { getLeague, getTeam, getPlayers, setAuctionTeam } from '../../data';
import {
    CircularProgress, Button, TextField, MenuItem, Typography,
    Paper, Grid, Checkbox, FormControlLabel, Chip,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import CheckIcon from '@material-ui/icons/Check';
import ErrorIcon from '@material-ui/icons/Error';

function TeamPanel({ teamName, squad, currentXI, playerBios, teamSize, onXIChange, saveStatus }) {
    const [selectedPlayers, setSelectedPlayers] = useState(currentXI);

    useEffect(() => {
        setSelectedPlayers(currentXI);
    }, [currentXI]);

    const handleToggle = (playerID) => {
        const next = selectedPlayers.includes(playerID)
            ? selectedPlayers.filter(id => id !== playerID)
            : [...selectedPlayers, playerID];
        setSelectedPlayers(next);
        onXIChange(teamName, next);
    };

    const getPlayerLabel = (playerID) => {
        const bio = playerBios.find(p => p.id === playerID);
        return bio ? `${bio.name} (${bio.team} - ${bio.pos})` : playerID;
    };

    const isValid = selectedPlayers.length === teamSize;
    const inXI = squad.filter(id => selectedPlayers.includes(id));
    const onBench = squad.filter(id => !selectedPlayers.includes(id));

    return (
        <Paper style={{ padding: 12, marginBottom: 12 }} variant="outlined" data-testid={`team-panel-${teamName}`}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                <Typography variant="subtitle1" style={{ flexGrow: 1, fontWeight: 600 }}>
                    {teamName}
                </Typography>
                <Chip
                    size="small"
                    label={`${selectedPlayers.length}/${teamSize}`}
                    color={isValid ? 'primary' : 'default'}
                    style={{ marginRight: 8 }}
                />
                {saveStatus === 'saved' && <CheckIcon color="primary" fontSize="small" data-testid={`saved-${teamName}`} />}
                {saveStatus === 'error' && <ErrorIcon color="error" fontSize="small" data-testid={`error-${teamName}`} />}
            </div>

            <Typography variant="body2" color="textSecondary" gutterBottom>
                Playing XI ({inXI.length})
            </Typography>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 0 }}>
                {inXI.map(playerID => (
                    <FormControlLabel
                        key={playerID}
                        control={
                            <Checkbox
                                checked={true}
                                onChange={() => handleToggle(playerID)}
                                size="small"
                                color="primary"
                                inputProps={{ 'data-testid': `${teamName}-player-${playerID}` }}
                            />
                        }
                        label={<Typography variant="body2">{getPlayerLabel(playerID)}</Typography>}
                        style={{ width: '100%', margin: 0 }}
                    />
                ))}
            </div>

            {onBench.length > 0 && (
                <>
                    <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }} gutterBottom>
                        Bench ({onBench.length})
                    </Typography>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 0 }}>
                        {onBench.map(playerID => (
                            <FormControlLabel
                                key={playerID}
                                control={
                                    <Checkbox
                                        checked={false}
                                        onChange={() => handleToggle(playerID)}
                                        size="small"
                                        color="primary"
                                        inputProps={{ 'data-testid': `${teamName}-player-${playerID}` }}
                                    />
                                }
                                label={<Typography variant="body2">{getPlayerLabel(playerID)}</Typography>}
                                style={{ width: '100%', margin: 0 }}
                            />
                        ))}
                    </div>
                </>
            )}
        </Paper>
    );
}

function AuctionAllTeams(props) {
    const leagueID = props.match.params.leagueID;
    const embedded = props.embedded || false;

    const [loading, setLoading] = useState(true);
    const [league, setLeague] = useState(null);
    const [teams, setTeams] = useState([]);
    const [teamDataMap, setTeamDataMap] = useState({});
    const [playerBios, setPlayerBios] = useState([]);
    const [gameWeek, setGameWeek] = useState('');
    const [teamSelections, setTeamSelections] = useState({});
    const [saveStatuses, setSaveStatuses] = useState({});
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [allSaved, setAllSaved] = useState(false);

    useEffect(() => {
        const load = async () => {
            try {
                const leagueData = await getLeague(leagueID);
                setLeague(leagueData);
                setTeams(leagueData.teams || []);
                const bios = await getPlayers(leagueData.tournament, leagueID);
                setPlayerBios(bios.players || []);
            } catch (err) {
                setError(err.message);
            }
            setLoading(false);
        };
        load();
    }, [leagueID]);

    const handleGameWeekChange = async (e) => {
        const gw = e.target.value;
        setGameWeek(gw);
        setAllSaved(false);
        setError(null);
        setSaveStatuses({});
        setTeamSelections({});

        const dataMap = {};
        for (const team of teams) {
            try {
                const data = await getTeam(leagueID, team.name);
                dataMap[team.name] = data;
            } catch (err) {
                console.error(`Failed to load team ${team.name}`, err);
            }
        }
        setTeamDataMap(dataMap);
    };

    const getExistingXI = (teamName) => {
        const teamData = teamDataMap[teamName];
        if (!teamData || !teamData[gameWeek]) return [];
        return teamData[gameWeek].players.map(p => p.id);
    };

    const handleXIChange = (teamName, players) => {
        setTeamSelections(prev => ({ ...prev, [teamName]: players }));
        setSaveStatuses(prev => {
            const next = { ...prev };
            delete next[teamName];
            return next;
        });
        setAllSaved(false);
    };

    const handleSaveAll = async () => {
        if (!gameWeek) {
            setError('Select a gameweek');
            return;
        }
        setSaving(true);
        setError(null);
        setAllSaved(false);
        const statuses = {};
        let hasError = false;

        for (const team of teams) {
            const selection = teamSelections[team.name] || getExistingXI(team.name);
            const teamSize = league?.teamSize || 11;
            if (selection.length !== teamSize) {
                statuses[team.name] = 'error';
                hasError = true;
                continue;
            }
            try {
                await setAuctionTeam(leagueID, team.name, gameWeek, selection);
                statuses[team.name] = 'saved';
            } catch (err) {
                statuses[team.name] = 'error';
                hasError = true;
            }
        }
        setSaveStatuses(statuses);
        if (hasError) {
            setError('Some teams failed to save. Check team panels for details.');
        } else {
            setAllSaved(true);
        }
        setSaving(false);
    };

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 40 }}>
                <CircularProgress />
            </div>
        );
    }

    const teamSize = league?.teamSize || 11;

    return (
        <div style={{ maxWidth: 700, margin: '0 auto', padding: 16, marginBottom: 60 }}>
            {!embedded && (
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
                    <Link to={`/leagues/${leagueID}`} style={{ marginRight: 8 }}>
                        <ArrowBackIcon />
                    </Link>
                    <Typography variant="h5" style={{ flexGrow: 1 }}>
                        Set All Teams - Gameweek
                    </Typography>
                </div>
            )}

            {error && <Alert severity="error" style={{ marginBottom: 16 }} data-testid="auction-error">{error}</Alert>}
            {allSaved && <Alert severity="success" style={{ marginBottom: 16 }} data-testid="auction-success">All teams saved successfully</Alert>}

            <Grid container spacing={2} style={{ marginBottom: 16 }}>
                <Grid item xs={5}>
                    <TextField
                        select
                        fullWidth
                        label="Gameweek"
                        value={gameWeek}
                        onChange={handleGameWeekChange}
                        SelectProps={{
                            SelectDisplayProps: { 'data-testid': 'auction-gameweek-select' }
                        }}
                    >
                        {Array.from({ length: 15 }, (_, i) => `gw${i + 1}`).map(gw => (
                            <MenuItem key={gw} value={gw}>{gw.toUpperCase()}</MenuItem>
                        ))}
                    </TextField>
                </Grid>
                <Grid item xs={7} style={{ display: 'flex', alignItems: 'center' }}>
                    {gameWeek && (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSaveAll}
                            disabled={saving}
                            data-testid="save-all-button"
                            startIcon={saving ? <CircularProgress size={16} /> : null}
                        >
                            {saving ? 'Saving...' : 'Save All Teams'}
                        </Button>
                    )}
                </Grid>
            </Grid>

            {gameWeek && teams.map(team => (
                <TeamPanel
                    key={team.name}
                    teamName={team.name}
                    squad={teamDataMap[team.name]?.squad || []}
                    currentXI={teamSelections[team.name] || getExistingXI(team.name)}
                    playerBios={playerBios}
                    teamSize={teamSize}
                    onXIChange={handleXIChange}
                    saveStatus={saveStatuses[team.name]}
                />
            ))}
        </div>
    );
}

export default withRouter(AuctionAllTeams);
